import React, { useEffect, useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  LayoutDashboard,
  Minus,
  Plus,
} from "lucide-react";
import FifoDetailRow from "./FifoDetailRow.jsx";
import { useLanguage } from "../contexts/LanguageContext.jsx";
import { t, getTaxCategoryLabel } from "../utils/translations";
import { formatDateFromTimestamp, formatDateTimeUtcFromTimestamp } from "../utils/dateUtils";
import { fmtTokenAmount, fmtEurPrice, fmtEurRow, fmtEurSummary } from "../utils/formatNumbers";
import MaskedEurCell from "./MaskedEurCell";
import {
  getPreviewTaxIssueKind,
  PreviewTaxIssueBadge,
  PreviewTaxIssueCell,
} from "./PreviewTaxIssue";
import { cn, tableTheme as T, analysisTone } from "./ui/analysisTableTheme.js";
import UnifiedTableEmptyState, { computeTableEmptyMetrics } from "./UnifiedTableEmptyState.jsx";
import { resolveDisplayTaxYearLabel } from "../utils/taxYear.js";
import { buildUniqueTaxRowKeys } from "../utils/taxRowKeys.js";
import {
  backendCurrencyOrFallback,
  currencySymbolOf,
  pickFiatTaxable,
  pickFiatPrice,
  pickFiatTotal,
  firstFinite,
} from "../utils/fiatFields";
import TaxTreatmentValue from "./TaxTreatmentValue.jsx";
import {
  shouldMaskPreviewEurRow,
  shouldShowPreviewSeparatorAfterRow,
} from "../utils/previewMode";
import PreviewTableNotice from "./PreviewTableNotice";
import PreviewModeSeparator from "./PreviewModeSeparator";
import { isUkCountry } from "../utils/ukTaxUi.js";

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
  } catch (e) {}
}

function safeLower(x) {
  return (x || "").toString().toLowerCase();
}

const MVA_SWAP_ICON = "/MVA.jpg";
const VAULTX_ICON = "/VAULTX.png";

const MVA_LIQUIDITY_POOLS = new Set([
  "0xccb1aeabc048dbf5a0545a1aa2ab56df112614b5", // MVA Swap (MVA/VET Pool)
]);

const VAULTX_LIQUIDITY_POOLS = new Set([
  "0xb54d6e6d1e67728ae2027e7f570d77c822214839", // VAULTX WOV/VET pool
]);

const LIQUIDITY_DEX_FALLBACKS = [
  {
    key: "vaultx",
    label: "VAULTX",
    icon: VAULTX_ICON,
    poolSet: VAULTX_LIQUIDITY_POOLS,
    hints: ["vaultx", "vault x", "vault-x", "vault_x"],
  },
  {
    key: "mva",
    label: "MVA Swap",
    icon: MVA_SWAP_ICON,
    poolSet: MVA_LIQUIDITY_POOLS,
    hints: ["mva", "mva swap", "mvalp", "mva-vet", "mva/vet"],
  },
  {
    key: "verocket",
    label: "VeRocket",
    icon: "/VeRocket.png",
    hints: ["verocket", "vrocket", "vrlp"],
  },
  {
    key: "vexchange",
    label: "Vexchange",
    icon: "/Vexchange.png",
    hints: ["vex-v2", "vex_v2", "vexv2", "vexchange"],
  },
  {
    key: "betterswap",
    label: "Betterswap",
    icon: "/Betterswap.png",
    hints: ["betlp", "better", "betterswap"],
  },
  {
    key: "turtleswap",
    label: "Turtleswap",
    icon: "/Turtleswap.png",
    hints: ["tsv2", "turtle", "turtleswap"],
  },
  {
    key: "veswap",
    label: "VeSwap",
    icon: "/VeSwap.jpg",
    hints: ["uni-v2", "uni_v2", "univ2", "veswap"],
  },
];

function isVaultXDex(key = "", label = "") {
  const k = safeLower(key);
  const l = safeLower(label);
  return (
    k === "vaultx" ||
    k === "vault_x" ||
    k === "vault-x" ||
    l === "vaultx" ||
    l === "vault x" ||
    l.includes("vaultx") ||
    l.includes("vault x")
  );
}

function isMvaDex(key = "", label = "") {
  const k = safeLower(key);
  const l = safeLower(label);
  return k === "mva" || l === "mva" || l === "mva swap" || l.includes("mva swap");
}

function normalizeLiquidityDexIcon(icon, key = "", label = "") {
  if (isVaultXDex(key, label)) return VAULTX_ICON;
  if (isMvaDex(key, label)) return MVA_SWAP_ICON;
  return icon || null;
}

function liquidityDexIconImgClass(icon, label = "") {
  if (icon === VAULTX_ICON || isVaultXDex("", label)) {
    return "h-3.5 w-auto max-w-[2.75rem] shrink-0 object-contain object-left";
  }

  return "h-5 w-5 shrink-0 object-contain";
}

function resolveLiquidityDexMeta(row = {}) {
  const pool = safeLower(
    row.pool ||
      row.pair ||
      row.pairAddress ||
      row.poolAddress ||
      row.liquidityPairAddress ||
      row.token_contract ||
      row.tokenContract ||
      row.contract ||
      ""
  );

  const lpSymbol = safeLower(
    row.lpSymbol ||
      row.lpTokenSymbol ||
      row.lp_token_symbol ||
      row.lpToken ||
      row.token_symbol ||
      row.tokenSymbol ||
      ""
  );

  const explicitKey = safeLower(
    row.liquidityDexKey ||
      row.liquidityDex ||
      row.dexKey ||
      row.dex ||
      ""
  );

  const explicitLabel =
    row.liquidityDexLabel ||
    row.dexLabel ||
    row.dexName ||
    row.protocolLabel ||
    row.protocol ||
    row.poolType ||
    row.poolLabel ||
    "";

  const explicitIcon = normalizeLiquidityDexIcon(
    row.liquidityDexIcon || row.dexIcon || row.protocolIcon || row.icon || "",
    explicitKey,
    explicitLabel
  );

  if (isVaultXDex(explicitKey, explicitLabel)) {
    return { key: "vaultx", label: "VAULTX", icon: VAULTX_ICON };
  }

  if (isMvaDex(explicitKey, explicitLabel)) {
    return { key: "mva", label: "MVA Swap", icon: MVA_SWAP_ICON };
  }

  for (const dex of LIQUIDITY_DEX_FALLBACKS) {
    if (explicitKey === dex.key || safeLower(explicitLabel) === safeLower(dex.label)) {
      return { key: dex.key, label: dex.label, icon: dex.icon };
    }

    if (dex.poolSet?.has(pool)) {
      return { key: dex.key, label: dex.label, icon: dex.icon };
    }

    if (
      dex.hints.some(
        (hint) =>
          lpSymbol.includes(hint) ||
          safeLower(explicitLabel).includes(hint)
      )
    ) {
      return { key: dex.key, label: dex.label, icon: dex.icon };
    }
  }

  if (explicitLabel || explicitIcon) {
    return {
      key: explicitKey || safeLower(explicitLabel) || "liquidity",
      label: explicitLabel || explicitKey || "Liquidity",
      icon: explicitIcon,
    };
  }

  return null;
}

function titleCase(s) {
  const str = (s || "").toString().trim();
  if (!str) return "";
  return str
    .replace(/_/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

function tokenKeyFromLeg(leg) {
  return (
    leg?.token ||
    leg?.tokenSymbol ||
    leg?.symbol ||
    leg?.ticker ||
    leg?.asset ||
    leg?.currency ||
    ""
  );
}

function legAmountDisplay(leg) {
  const v =
    leg?.amountDisplay ??
    leg?.amount_display ??
    leg?.amount ??
    leg?.valueDisplay ??
    leg?.value_display ??
    leg?.value;

  if (v === null || v === undefined || v === "") return "";
  if (typeof v === "string") return v;

  const num = Number(v);
  if (!Number.isFinite(num)) return String(v);
  // ✅ LiquidityTaxTable: Volle Zahl anzeigen (keine Rundung auf 6 dp)
  // Verwende toFixed mit hoher Präzision, dann trimme nur trailing zeros
  const s = num.toFixed(18);
  return s.replace(/\.?0+$/, "");
}

function legEurValue(leg) {
  const v =
    leg?.valueFiat ??
    leg?.eur ??
    leg?.eurValue ??
    leg?.eur_value ??
    leg?.valueEUR ??
    leg?.value_eur ??
    leg?.euro ??
    leg?.euroValue ??
    leg?.euroAmount ??
    leg?.euro_amount ??
    leg?.amountEUR ??
    leg?.amount_eur;

  const num = Number(v);
  if (Number.isFinite(num) && num !== 0) return num;

  const euroPrice = Number(
    leg?.unitPriceFiat ??
      leg?.euroPrice ??
      leg?.unitEur ??
      leg?.unit_cost_eur ??
      leg?.unit_cost ??
      leg?.priceEUR ??
      leg?.price_eur ??
      0
  );
  const amount = Number(leg?.amount ?? leg?.qty ?? leg?.quantity ?? 0);
  const computed = euroPrice * amount;

  return Number.isFinite(computed) ? computed : 0;
}

function legEurUnitPrice(leg) {
  // 1) Wenn ein expliziter Preis existiert: verwenden
  const direct = Number(
    leg?.unitPriceFiat ??
      leg?.euroPrice ??
      leg?.unitEur ??
      leg?.unit_cost_eur ??
      leg?.unit_cost ??
      leg?.priceEUR ??
      leg?.price_eur ??
      0
  );
  if (Number.isFinite(direct) && direct !== 0) return direct;

  // 2) Sonst ableiten aus (eurValue / amount)
  const amount = Number(leg?.amount ?? leg?.qty ?? leg?.quantity ?? 0);
  const eur = legEurValue(leg);

  if (Number.isFinite(amount) && amount !== 0 && Number.isFinite(eur) && eur !== 0) {
    return eur / amount;
  }
  return 0;
}

function fmtEurUnitPrice(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return "";

  const a = Math.abs(n);

  // Smart Precision für kleine Tokenpreise
  let dp = 6;
  if (a >= 100) dp = 2;
  else if (a >= 1) dp = 4;
  else if (a < 0.01) dp = 8;
  if (a < 0.0001) dp = 10;

  const s = n.toFixed(dp);
  return s.replace(/\.?0+$/, "");
}

function unitPriceSummary(legs, isMasked, currencySymbol) {
  if (!Array.isArray(legs) || legs.length === 0) return "";

  const parts = legs
    .map((l) => {
      const sym = tokenKeyFromLeg(l);
      if (!sym) return null;

      const p = legEurUnitPrice(l);
      if (!Number.isFinite(p) || p === 0) return null;

      if (isMasked) {
        return `${sym} @ **.**`;
      }
      return `${sym} @ ${currencySymbol} ${fmtEurPrice(p)}`;
    })
    .filter(Boolean);

  return parts.join(" | ");
}

function sumEur(legs) {
  return (legs || []).reduce((acc, l) => acc + legEurValue(l), 0);
}

function splitDateTimeLikeOtherTables(ts, { utc = false } = {}) {
  const full = (utc ? formatDateTimeUtcFromTimestamp(ts) : formatDateFromTimestamp(ts)) || "";
  // typischerweise "DD.MM.YYYY, HH:mm:ss" oder "DD.MM.YYYY HH:mm:ss"
  if (full.includes(",")) {
    const [d, t] = full.split(",").map((x) => x.trim());
    return { d: d || full, t: t || "" };
  }
  const parts = full.split(" ").filter(Boolean);
  if (parts.length >= 2) return { d: parts[0], t: parts.slice(1).join(" ") };
  return { d: full, t: "" };
}

// 🚀 Helper: Eindeutigen, stabilen Row-Key generieren
function getRowIdentity(r) {
  // best case: Backend liefert eine eindeutige ID
  if (r.rowId) return String(r.rowId);
  if (r.event_id) return `${r.event_id}`;
  if (r.logIndex != null && r.txid) return `${r.txid}-${r.logIndex}`;

  // fallback: so breit wie möglich (möglichst stabil und unique)
  const txid = r.txid || r.hash || r.txHash || r.transactionHash || "";
  return [
    txid || "nohash",
    r.kind || r.action || r.type || r.classification || "nokind",
    r.pool || "nopool",
    r.timestamp || "nots",
  ].join("|");
}


// ⚠️ Helper: Prüft ob Fiat-Preis fehlt (robust: nicht nur euroPrice prüfen, sondern auch Flags)
function hasFiatPrice(v) {
  const n = Number(v);
  return Number.isFinite(n) && n > 0;
}

function isMissingFiatForTx(tx) {
  const explicitMissing = Boolean(
    tx?.pricingMissing ?? tx?.priceMissing ?? tx?.missingFiatPrice
  );

  // Hotfix: Add-Liquidity benötigt keinen eigenen Row-Euro-Preis.
  // Die Legs (Deposit) haben bereits EUR/Unit-Preise; daher keinen "Token price not available"-Fehler anzeigen.
  if (explicitMissing) return true;
  if (tx?.kind === "add") return false;

  const disposals = Array.isArray(tx?.disposals) ? tx.disposals : [];
  const withdrawals = Array.isArray(tx?.withdrawals) ? tx.withdrawals : [];

  const hasDisposalPrice = disposals.some((d) =>
    hasFiatPrice(
      d?.unitPriceFiat ??
        d?.euroPrice ??
        d?.unitEur ??
        d?.sellUnitPriceEur ??
        d?.sell_unit_price_eur
    )
  );

  const hasWithdrawalPrice = withdrawals.some((l) =>
    hasFiatPrice(
      l?.euroPrice ?? l?.unitEur ?? l?.priceEUR ?? l?.price_eur
    )
  );

  return !(hasDisposalPrice || hasWithdrawalPrice);
}

// ⚠️ Kompakter Hinweis für fehlende Token-Preise
function UnsupportedTokenHint({ language, align = "center" }) {
  return (
    <span
      className={[
        "mt-1 inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800 whitespace-nowrap",
        align === "end" ? "justify-end self-end" : "justify-center self-center",
      ].join(" ")}
    >
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500" />
      <span>
        {language === "en"
          ? "Price unavailable · excluded"
          : "Preis fehlt · nicht einbezogen"}
      </span>
    </span>
  );
}

function normalizeLiquidityRow(r, selectedCountry = "DE", totals = {}) {
  const txid = r.txid || r.hash || r.txHash || r.transactionHash || "";
  const timestamp = Number(r.timestamp || r.time || r.ts || 0);

  const pool = r.pool || r.pair || r.pairAddress || r.poolAddress || r.poolAddress || "";
  const poolTypeRaw = r.poolType || r.dex || r.protocol || r.poolLabel || r.poolTag || "";

  const rawKind = safeLower(r.kind || r.action || r.type || r.classification || r.direction || r.event);
  let kind = "unknown";
  if (rawKind.includes("add")) kind = "add";
  else if (rawKind.includes("remove")) kind = "remove";

  const deposits = Array.isArray(r.deposits)
    ? r.deposits
    : Array.isArray(r.depositTokens)
      ? r.depositTokens
      : Array.isArray(r.deposit)
        ? r.deposit
        : [];

  const withdrawals = Array.isArray(r.withdrawals)
    ? r.withdrawals
    : Array.isArray(r.withdrawTokens)
      ? r.withdrawTokens
      : Array.isArray(r.withdraw)
        ? r.withdraw
        : [];

  const lpSymbol = r.lpSymbol || r.lpTokenSymbol || r.lp_token_symbol || r.lpToken || r.lp || "";

  const dexMeta = resolveLiquidityDexMeta({
    ...r,
    pool,
    poolType: poolTypeRaw,
    lpSymbol,
  });

  const poolType = dexMeta?.label || poolTypeRaw;

  const lpAmountDisplay =
    (r.lpAmountDisplay ?? r.lp_amount_display ?? r.lpDisplay ?? r.lp_display ?? r.lpAmount ?? "")?.toString() || "";
  const lpAmountRaw = (r.lpAmountRaw ?? r.lp_amount_raw ?? r.amountRaw ?? r.amount_raw ?? "")?.toString() || "";

  const disposalProfit =
    pickFiatTaxable(r) ??
    firstFinite(
      r.disposalProfitFiat,
      r.disposalProfitEUR,
      r.taxableProfitFiat,
      r.taxableProfitEUR,
      r.netProfitEUR,
      r.profitEUR,
      r.profitEur,
      0
    ) ??
    0;

  const disposals = Array.isArray(r.disposals) ? r.disposals : Array.isArray(r.disposalTxs) ? r.disposalTxs : [];

  return {
    ...r,
    txid,
    timestamp,
    pool,
    poolType,
    liquidityDex: dexMeta?.key || r.liquidityDex || r.dex || "",
    liquidityDexKey: dexMeta?.key || r.liquidityDexKey || r.dexKey || "",
    liquidityDexLabel: dexMeta?.label || r.liquidityDexLabel || r.dexLabel || poolTypeRaw || "",
    liquidityDexIcon:
      dexMeta?.icon ||
      normalizeLiquidityDexIcon(
        r.liquidityDexIcon || r.dexIcon || "",
        dexMeta?.key,
        dexMeta?.label
      ),
    kind,
    deposits,
    withdrawals,
    lpSymbol,
    lpAmountDisplay,
    lpAmountRaw,
    disposalProfitFiat: disposalProfit,
    disposalProfitEUR: Number.isFinite(disposalProfit) ? disposalProfit : 0,
    fiatCurrency: backendCurrencyOrFallback(selectedCountry, r, totals),
    disposals,
  };
}

function buildDisposalTx(disposal, fallbackTs, fallbackTxid) {
  const token =
    disposal?.tokenSymbol ||
    disposal?.symbol ||
    disposal?.token ||
    disposal?.token_name ||
    "";

  const amount =
    disposal?.amount ??
    disposal?.qty ??
    disposal?.quantity ??
    disposal?.usedAmount ??
    disposal?.outAmount ??
    0;

  let fiatPrice = pickFiatPrice(disposal);
  const fiatAmount = pickFiatTotal(disposal);

  if (!Number.isFinite(Number(fiatPrice)) || Number(fiatPrice) === 0) {
    const amtNum = Number(amount) || 0;
    if (amtNum > 0 && Number.isFinite(fiatAmount) && fiatAmount > 0) {
      fiatPrice = fiatAmount / amtNum;
    } else {
      fiatPrice = 0;
    }
  }

  const timestamp = Number(disposal?.timestamp || disposal?.ts || fallbackTs || 0);

  const inflows =
    Array.isArray(disposal?.matchedInflows) ? disposal.matchedInflows :
    Array.isArray(disposal?.fifoLegs) ? disposal.fifoLegs :
    Array.isArray(disposal?.inflows) ? disposal.inflows :
    [];

  const fifoLegs = Array.isArray(disposal?.fifoLegs)
    ? disposal.fifoLegs
    : Array.isArray(disposal?.matchedInflows)
      ? disposal.matchedInflows
      : [];

  const grossBuy = firstFinite(
    disposal?.grossBuyFiat,
    disposal?.grossBuyEUR,
    disposal?.costBasisFiat,
    disposal?.costFiat
  );
  const grossSell = firstFinite(
    disposal?.grossSellFiat,
    disposal?.grossSellEUR,
    disposal?.proceedsFiat,
    fiatAmount
  );
  const grossProfit = firstFinite(
    disposal?.grossProfitFiat,
    disposal?.grossProfitEUR,
    disposal?.taxableProfitFiat,
    disposal?.taxableProfitEUR,
    disposal?.taxableProfit,
    grossSell != null && grossBuy != null ? grossSell - grossBuy : null
  );
  const taxableProfit = firstFinite(
    disposal?.taxableProfitFiat,
    disposal?.taxableProfitEUR,
    disposal?.taxableProfit,
    grossProfit
  );

  return {
    ...disposal,
    hash: disposal?.hash || fallbackTxid || "",
    token,
    amount: Number(amount) || 0,
    unitPriceFiat: Number(fiatPrice) || 0,
    amountFiat: Number.isFinite(fiatAmount) ? fiatAmount : 0,
    euroPrice: Number(fiatPrice) || 0,
    euroAmount: Number.isFinite(fiatAmount) ? fiatAmount : 0,
    grossBuyFiat: grossBuy,
    grossBuyEUR: grossBuy,
    grossSellFiat: grossSell,
    grossSellEUR: grossSell,
    grossProfitFiat: grossProfit,
    grossProfitEUR: grossProfit,
    taxableProfitFiat: taxableProfit,
    taxableProfitEUR: taxableProfit,
    taxableProfit,
    timestamp,
    fifoLegs,
    __inflows: inflows
  };
}

export default function LiquidityTaxTable({
  year,
  displayTaxYear,
  liquidityDataFromProps,
  liquidityTotalsFromProps,
  onTotalProfitCalculated,
  onDataReady,
  selectedCountry,
  pdfExportMode = false,
  isPreviewMode = false,
  enableExportCallback = true,
}) {
  const { language } = useLanguage();
  const tone = analysisTone.violet;
  const primaryTableWrapClass = "v3-primary-table-shell v3-primary-table-shell--sky";
  const primaryTableHeadClass = "v3-primary-table-head v3-primary-table-head--sky";

  const cc = String(selectedCountry || "").toUpperCase();
  const isUS = cc === "US" || cc === "USA" || cc.includes("UNITED STATES");
  const isUK = isUkCountry(selectedCountry);
  const yearLabel = resolveDisplayTaxYearLabel(year, selectedCountry, displayTaxYear);
  const tr = (key, fallback) => {
    const v = t(key, language);
    return !v || v === key ? fallback : v;
  };

  const api = typeof window !== "undefined" ? window.__analysisApiData : null;

  const rawRows =
    liquidityDataFromProps ??
    api?.analysis?.liquidityTaxRows ??
    api?.liquidityTaxRows ??
    [];

  const totals =
    liquidityTotalsFromProps ??
    api?.analysis?.liquidityTotals ??
    api?.liquidityTotals ??
    {};

  const totalsCurrency = backendCurrencyOrFallback(selectedCountry, totals);
  const currencyCode = totalsCurrency;
  const currencySymbol = currencySymbolOf(currencyCode);

  const rows = useMemo(
    () =>
      (rawRows || []).map((r) =>
        normalizeLiquidityRow(r, selectedCountry, totals)
      ),
    [rawRows, selectedCountry, totals]
  );

  // PDF: Zeilen kommen synchron aus Props (TaxReportPrint filtert vorab) — kein useEffect-Race
  const [search, setSearch] = useState("");
  const [poolFilter, setPoolFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [expandedKey, setExpandedKey] = useState(null);

  const pools = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => { if (r.pool) set.add(r.pool); });
    return ["all", ...Array.from(set)];
  }, [rows]);

  const filtered = useMemo(() => {
    const q = safeLower(search);
    return rows
      .filter((r) => {
        if (poolFilter !== "all" && r.pool !== poolFilter) return false;
        if (typeFilter !== "all" && r.kind !== typeFilter) return false;

        if (!q) return true;

        const hay = [
          r.txid,
          r.pool,
          r.poolType,
          r.liquidityDexLabel,
          r.liquidityDexKey,
          r.lpSymbol,
          r.kind,
          ...((r.deposits || []).map((l) => `${tokenKeyFromLeg(l)} ${legAmountDisplay(l)}`)),
          ...((r.withdrawals || []).map((l) => `${tokenKeyFromLeg(l)} ${legAmountDisplay(l)}`))
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return hay.includes(q);
      })
      .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
  }, [rows, search, poolFilter, typeFilter]);

  const rowKeys = useMemo(
    () => buildUniqueTaxRowKeys(filtered, getRowIdentity),
    [filtered]
  );

  const emptyMetrics = useMemo(
    () =>
      computeTableEmptyMetrics({
        allRows: rawRows,
        yearRows: rows,
        filteredRows: filtered,
        effectiveYear: String(year ?? ""),
      }),
    [rawRows, rows, filtered, year]
  );

  const totalProfit = useMemo(() => {
    const sum = filtered.reduce((acc, r) => acc + (Number(r.disposalProfitEUR) || 0), 0);
    return Number.isFinite(sum) ? sum : 0;
  }, [filtered]);

  useEffect(() => {
    if (typeof onTotalProfitCalculated === "function") onTotalProfitCalculated(totalProfit);
  }, [totalProfit, onTotalProfitCalculated]);

  useEffect(() => {
    if (typeof onDataReady === "function" && enableExportCallback) {
      const exportData = filtered.map((r) => ({
        hash: r.txid || r.hash || "",
        type: r.kind || r.event || r.classification || "",
        token: r.token,
        amount: r.amount,
        timestamp: r.timestamp,
        ts: r.ts,
        classification: r.classification,
        kind: r.kind,
        event: r.event,
      }));
      onDataReady(exportData);
    }
  }, [filtered, onDataReady, enableExportCallback]);

  // 🚀 Reset Expand-State bei Filter/Sort-Wechsel
  useEffect(() => {
    setExpandedKey(null);
  }, [poolFilter, typeFilter, search]);

  function toggleRow(rowKey) {
    if (pdfExportMode) return;
    setExpandedKey(prev => (prev === rowKey ? null : rowKey));
  }

  function renderLegSummary(legs) {
    if (!legs || legs.length === 0) return "—";
    return legs
      .map((l) => `${legAmountDisplay(l)} ${tokenKeyFromLeg(l)}`.trim())
      .join(" + ");
  }

  function getLiquidityActionUi(kind) {
    const k = String(kind || "").toLowerCase();

    if (k === "add") {
      return {
        label: "Add",
        eyebrow: "Liquidity",
        Icon: Plus,
        rail: "from-emerald-400 to-teal-500",
        iconBox: "border-emerald-100 bg-emerald-50 text-emerald-700",
        eyebrowText: "text-emerald-700",
        hover:
          "hover:border-emerald-200 hover:bg-gradient-to-br hover:from-white hover:to-emerald-50/50 hover:shadow-emerald-900/10",
        focus: "focus:ring-emerald-100",
      };
    }

    if (k === "remove") {
      return {
        label: "Remove",
        eyebrow: "Liquidity",
        Icon: Minus,
        rail: "from-orange-400 to-amber-500",
        iconBox: "border-orange-100 bg-orange-50 text-orange-700",
        eyebrowText: "text-orange-700",
        hover:
          "hover:border-orange-200 hover:bg-gradient-to-br hover:from-white hover:to-orange-50/50 hover:shadow-orange-900/10",
        focus: "focus:ring-orange-100",
      };
    }

    return {
      label: titleCase(kind || "Liquidity"),
      eyebrow: "Liquidity",
      Icon: LayoutDashboard,
      rail: "from-blue-500 to-indigo-600",
      iconBox: "border-blue-100 bg-blue-50 text-blue-700",
      eyebrowText: "text-blue-700",
      hover:
        "hover:border-blue-200 hover:bg-gradient-to-br hover:from-white hover:to-blue-50/50 hover:shadow-blue-900/10",
      focus: "focus:ring-blue-100",
    };
  }

  function renderTypePill(r, rowKey) {
    const expanded = pdfExportMode ? true : expandedKey === rowKey;
    const ui = getLiquidityActionUi(r.kind);
    const ActionIcon = ui.Icon;
    const ChevronIcon = expanded ? ChevronDown : ChevronRight;

    if (pdfExportMode) {
      return (
        <span className="inline-flex items-center gap-1 px-0 py-0 text-[6.7pt] leading-[1.12] font-semibold text-black">
          <span>{expanded ? "▼" : "▶"}</span>
          <span>{ui.label}</span>
        </span>
      );
    }

    return (
      <button
        type="button"
        onClick={() => toggleRow(rowKey)}
        className={cn(
          "group relative inline-flex min-w-[118px] items-center gap-2 overflow-hidden rounded-2xl border border-slate-200/90 bg-white px-3 py-2.5 text-left shadow-[0_1px_2px_rgba(15,23,42,0.04)] ring-1 ring-white/80 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_26px_rgba(15,23,42,0.08)] focus:outline-none focus:ring-2",
          ui.hover,
          ui.focus
        )}
        title={`${ui.eyebrow} · ${ui.label}`}
      >
        <span
          className={cn(
            "absolute left-0 top-2 bottom-2 w-1 rounded-r-full bg-gradient-to-b",
            ui.rail
          )}
        />

        <span
          className={cn(
            "ml-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-xl border shadow-sm transition-transform duration-200 group-hover:scale-[1.03]",
            ui.iconBox
          )}
        >
          <ActionIcon className="h-3.5 w-3.5" strokeWidth={2.4} />
        </span>

        <span className="min-w-0 flex-1 leading-tight">
          <span
            className={cn(
              "block text-[9px] font-bold uppercase tracking-[0.16em]",
              ui.eyebrowText
            )}
          >
            {ui.eyebrow}
          </span>
          <span className="block truncate text-sm font-semibold tracking-[-0.01em] text-slate-950">
            {ui.label}
          </span>
        </span>

        <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-slate-500 transition-colors duration-200 group-hover:border-slate-300 group-hover:bg-white group-hover:text-slate-700">
          <ChevronIcon className="h-3.5 w-3.5" strokeWidth={2.4} />
        </span>
      </button>
    );
  }

  function renderTxHashCell(r) {
    const full = (r.txid || "").toString();

    if (pdfExportMode) {
      return (
        <span
          className="block w-full font-mono text-black whitespace-normal break-all pdf-break-all"
          title={full || "—"}
        >
          {full || "—"}
        </span>
      );
    }

    const short = full ? `${full.slice(0, 10)}…${full.slice(-6)}` : "—";
    return (
      <button
        type="button"
        onClick={() => copyToClipboard(full)}
        className="block w-full font-mono text-slate-700 hover:text-indigo-600 hover:underline whitespace-normal break-all"
        title={full || "—"}
      >
        {full || "—"}
      </button>
    );
  }

  function renderPoolCell(r) {
    const pool = (r.pool || "").toString();
    const label = r.poolType || (r.lpSymbol ? r.lpSymbol : "");
    const dexLabel = r.liquidityDexLabel || "";
    const dexIcon = r.liquidityDexIcon || null;
    const showSubLabel = label && label !== dexLabel;

    return (
      <div className="whitespace-normal break-all">
        {dexLabel || dexIcon ? (
          <div className="mb-2 inline-flex max-w-full items-center gap-1.5 rounded-full border border-slate-200/90 bg-white px-2.5 py-1 text-xs font-semibold text-slate-800 shadow-[0_1px_2px_rgba(15,23,42,0.04)] ring-1 ring-white/80">
            {dexIcon ? (
              <img
                src={dexIcon}
                alt={dexLabel || "Liquidity"}
                className={liquidityDexIconImgClass(dexIcon, dexLabel)}
                draggable={false}
                onError={(e) => {
                  console.warn("[LiquidityTaxTable] Liquidity icon missing:", dexIcon);
                  e.currentTarget.style.display = "none";
                }}
              />
            ) : null}

            <span className="truncate">{dexLabel || "Liquidity"}</span>
          </div>
        ) : null}

        <div className="font-mono text-xs font-semibold leading-relaxed text-slate-700 whitespace-normal break-all">
          {pool || "—"}
        </div>

        {showSubLabel ? (
          <div className="mt-1 text-[11px] font-medium text-slate-400">
            {label}
          </div>
        ) : null}
      </div>
    );
  }

  function renderDateCell(r) {
    const { d, t: tt } = splitDateTimeLikeOtherTables(r.timestamp, { utc: pdfExportMode });
    return (
      <div className="text-slate-600 text-center leading-tight whitespace-normal">
        <div>{d}{tt ? "," : ""}</div>
        {tt ? <div>{tt}</div> : null}
      </div>
    );
  }

  function renderLegCards(legs, theme, maskRowEur = false) {
    const isMasked = !!maskRowEur;
    if (!legs || legs.length === 0) return null;

    const total = sumEur(legs);

    return (
      <div className={`rounded-lg border ${theme.border} ${theme.bg}`}>
        <div className={`flex items-center justify-between px-3 py-2 border-b ${theme.border}`}>
          <div className={`text-sm font-semibold ${theme.title}`}>{theme.titleText}</div>
          <div className="text-sm font-bold">
            <MaskedEurCell currencySymbol={currencySymbol} currencyCode={currencyCode} language={language} value={isMasked ? `${currencySymbol} **.**` : `${currencySymbol} ${fmtEurRow(total)}`} isMasked={isMasked} fullWidth />
          </div>
        </div>

        <div className="p-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {legs.map((l, idx) => {
              const sym = tokenKeyFromLeg(l) || "—";
              const amt = legAmountDisplay(l);
              const eur = legEurValue(l);

              const unit = legEurUnitPrice(l);
              const unitTxt = !isMasked ? fmtEurUnitPrice(unit) : "**.**";
              const showUnit = Number.isFinite(unit) && unit !== 0;

              return (
                <div key={`${sym}-${idx}`} className="rounded-md border border-gray-200 bg-white px-3 py-2">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-semibold text-gray-900">{sym}</div>
                    <div className="font-mono text-xs text-gray-900 whitespace-normal break-all">
                      {amt || "—"}
                    </div>
                  </div>

                  {/* ✅ Unit-Preis (€/Token) */}
                  {showUnit ? (
                    <div className="mt-0.5 text-[10px] text-gray-400">
                      @ {currencySymbol} {fmtEurPrice(unit)} / {sym}
                    </div>
                  ) : null}

                  {/* ✅ Leg-Wert (EUR) */}
                  <div className="mt-1 text-[11px] text-gray-500">
                    <MaskedEurCell currencySymbol={currencySymbol} currencyCode={currencyCode} language={language} value={isMasked ? `${currencySymbol} **.**` : `${currencySymbol} ${fmtEurRow(eur)}`} isMasked={isMasked} fullWidth />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  const taxLabel = getTaxCategoryLabel("disposal", selectedCountry, language);
  const totalFooter = Number(
    firstFinite(
      totals?.disposalNetFiat,
      totals?.totalTaxableFiat,
      totals?.taxableNetFiat,
      totals?.disposalNetEUR,
      totals?.disposalProfitEUR,
      totals?.netProfitEUR,
      totalProfit,
      0
    ) || 0
  );


  const renderLiquidityFooter = () => (
    <tr className={cn("font-semibold border-t", tone.footerPrimary)}>
      <td colSpan={6} className="px-6 py-4 text-right">
        {getTaxCategoryLabel("disposal", selectedCountry, language)} (Liquidity) – Summe {tr("taxYear", "Steuerjahr")} {yearLabel}:
      </td>
      <td className="px-6 py-4 font-mono">
        <MaskedEurCell currencySymbol={currencySymbol} currencyCode={currencyCode} language={language} value={isPreviewMode ? `${currencySymbol} **.**` : `${currencySymbol} ${fmtEurSummary(totalFooter)}`} isMasked={!!isPreviewMode} fullWidth />
      </td>
      <td className="px-6 py-4" />
    </tr>
  );

  const renderLiquidityRow = (r, index, { virtualMode = false } = {}) => {
              const maskRowEur = shouldMaskPreviewEurRow(index, filtered.length, isPreviewMode);
              const rowKey = rowKeys[index] ?? getRowIdentity(r);
              const expanded = pdfExportMode ? true : expandedKey === rowKey;
              const isMasked = !!maskRowEur;

              const depositText = r.kind === "add" ? renderLegSummary(r.deposits) : "—";
              const withdrawText = r.kind === "remove" ? renderLegSummary(r.withdrawals) : "—";

              const depositEur = r.kind === "add" ? sumEur(r.deposits) : 0;
              const withdrawEur = r.kind === "remove" ? sumEur(r.withdrawals) : 0;

              const depositUnitSummary = r.kind === "add" ? unitPriceSummary(r.deposits, isMasked, currencySymbol) : "";
              const withdrawUnitSummary = r.kind === "remove" ? unitPriceSummary(r.withdrawals, isMasked, currencySymbol) : "";

              const lpDisplay = r.lpAmountDisplay || r.lpAmountRaw || "";
              const missing = isMissingFiatForTx(r);
              const previewIssueKind = getPreviewTaxIssueKind(r);
              const showPreviewIssueRow = isMasked && !!previewIssueKind;

              const showDetails = pdfExportMode
                ? expanded &&
                  ((r.kind === "add" && Array.isArray(r.deposits) && r.deposits.length > 0) ||
                    (r.kind === "remove" && Array.isArray(r.withdrawals) && r.withdrawals.length > 0))
                : expanded;

              const rowInner = (
                <>
                  {pdfExportMode && showDetails ? (
                    <tr className="pdf-liquidity-row-head">
                      <th className={cn(T.th, "text-center")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("type", language)}</span>
                        </span>
                      </th>
                      <th className={cn(T.th, "text-center")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("txHashColumn", language)}</span>
                        </span>
                      </th>
                      <th className={cn(T.th, "text-center")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-fix")}>Pool</span>
                        </span>
                      </th>
                      <th className={cn(T.th, "text-center")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("lpDepositColumn", language)}</span>
                          <span className={cn(T.thSub, "v3-th-sub-fix")}>{t("tokensColumnSub", language)}</span>
                        </span>
                      </th>
                      <th className={cn(T.th, "text-center")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("lpWithdrawColumn", language)}</span>
                          <span className={cn(T.thSub, "v3-th-sub-fix")}>{t("tokensColumnSub", language)}</span>
                        </span>
                      </th>
                      <th className={cn(T.th, "text-center")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-fix")}>LP</span>
                        </span>
                      </th>
                      <th className={cn(T.th, "text-center")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("taxableProfitShort", language)}</span>
                          <span className={cn(T.thSub, "v3-th-sub-fix")}>({currencyCode})</span>
                        </span>
                      </th>
                      <th className={cn(T.th, "text-center")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-fix")}>{pdfExportMode ? (language === "en" ? "Date (UTC)" : "Datum (UTC)") : t("date", language)}</span>
                        </span>
                      </th>
                    </tr>
                  ) : null}
                  <tr
                    className={cn(
                      T.tr,
                      "group pdf-liquidity-main-row transition-colors duration-150",
                      missing
                        ? "bg-amber-50/30 hover:bg-amber-50/60"
                        : "hover:bg-slate-50/80"
                    )}
                  >
                    <td className={T.td}>{renderTypePill(r, rowKey)}</td>
                    <td className={cn(T.td, "whitespace-normal break-all")}>
                      {renderTxHashCell(r)}
                    </td>
                    <td className={cn(T.td, "whitespace-normal break-all")}>
                      {renderPoolCell(r)}
                    </td>

                    {showPreviewIssueRow ? (
                      <PreviewTaxIssueCell
                        language={language}
                        issueKind={previewIssueKind}
                        colSpan={4}
                      />
                    ) : (
                      <>
                        <td className={T.td}>
                          <div className="whitespace-normal break-words text-slate-800">{depositText}</div>
                          {r.kind === "add" ? (
                            <>
                              <div className="mt-1 text-xs text-slate-500">
                                {missing ? (
                                  <span className="font-mono text-slate-400">—</span>
                                ) : (
                                  <MaskedEurCell
                                    currencySymbol={currencySymbol}
                                    currencyCode={currencyCode}
                                    language={language}
                                    value={isMasked ? `${currencySymbol} **.**` : `${currencySymbol} ${fmtEurRow(depositEur)}`}
                                    isMasked={isMasked}
                                    fullWidth
                                  />
                                )}
                              </div>
                              {depositUnitSummary ? (
                                <div className="mt-0.5 text-[11px] text-slate-400 whitespace-normal break-words">
                                  {depositUnitSummary}
                                </div>
                              ) : null}
                            </>
                          ) : null}
                        </td>

                        <td className={T.td}>
                          <div className="whitespace-normal break-words text-slate-800">{withdrawText}</div>
                          {r.kind === "remove" ? (
                            <>
                              <div className="mt-1 text-xs text-slate-500">
                                {missing ? (
                                  <span className="font-mono text-slate-400">—</span>
                                ) : (
                                  <MaskedEurCell
                                    currencySymbol={currencySymbol}
                                    currencyCode={currencyCode}
                                    language={language}
                                    value={isMasked ? `${currencySymbol} **.**` : `${currencySymbol} ${fmtEurRow(withdrawEur)}`}
                                    isMasked={isMasked}
                                    fullWidth
                                  />
                                )}
                              </div>
                              {withdrawUnitSummary ? (
                                <div className="mt-0.5 text-[11px] text-slate-400 whitespace-normal break-words">
                                  {withdrawUnitSummary}
                                </div>
                              ) : null}
                            </>
                          ) : null}
                        </td>

                        <td className={T.td}>
                          <div className="font-mono text-slate-800 break-all" title={lpDisplay || "—"}>
                            {lpDisplay || "—"}
                          </div>
                          {r.lpSymbol ? <div className="mt-1 text-xs text-slate-500">{r.lpSymbol}</div> : null}
                        </td>

                        <td className={cn(T.td, "text-center text-slate-900")}>
                          {isMasked ? (
                            <MaskedEurCell
                              currencySymbol={currencySymbol}
                              currencyCode={currencyCode}
                              language={language}
                              value={`${currencySymbol} **.**`}
                              isMasked={true}
                              fullWidth
                            />
                          ) : missing ? (
                            <div className="flex flex-col items-center">
                              <span className="font-mono text-slate-400">—</span>
                              <UnsupportedTokenHint language={language} />
                            </div>
                          ) : (
                            <TaxTreatmentValue
                              row={r}
                              language={language}
                              selectedCountry={selectedCountry}
                              treatment="disposal"
                              formattedValue={`${currencySymbol} ${fmtEurRow(
                                r.disposalProfitEUR ??
                                  r.taxableProfitEUR ??
                                  r.netProfitEUR ??
                                  r.profitEUR ??
                                  0
                              )}`}
                            />
                          )}
                        </td>
                      </>
                    )}

                    <td className={T.td}>{renderDateCell(r)}</td>
                  </tr>

                  {expanded ? (
                    <tr className="pdf-liquidity-detail-row pdf-tx-detail-row bg-white">
                      <td colSpan={8} className="px-6 py-4 border-t border-slate-200/80">
                        {pdfExportMode ? (
                          <div className="space-y-2 text-black">
                            <div className="text-[6.7pt] font-semibold leading-[1.12] mb-1">
                              {taxLabel} (Liquidity)
                            </div>
                            <div className="space-y-2">
                                {(r.disposals || []).length === 0 ? (
                                  <div className="text-[6.7pt] leading-[1.12] text-black">
                                    {language === "en" ? "No disposals available." : "Keine Disposals vorhanden."}
                                  </div>
                                ) : (
                                  (r.disposals || []).map((d, i) => {
                                    const dtx = buildDisposalTx(d, r.timestamp, r.txid);
                                    const inflows = Array.isArray(dtx.__inflows) ? dtx.__inflows : [];
                                    return (
                                      <table key={`${dtx.token || "token"}-${i}`} className="w-full">
                                        <tbody>
                                          <FifoDetailRow
                                            tx={dtx}
                                            inflows={inflows}
                                            colSpan={1}
                                            fullPrecisionAmounts={true}
                                            fullPrecisionDp={18}
                                            selectedCountry={selectedCountry}
                                            isPreviewMode={isPreviewMode}
                                            maskRowEur={isMasked}
                                            pdfExportMode={true}
                                            language={language}
                                          />
                                        </tbody>
                                      </table>
                                    );
                                  })
                                )}
                            </div>
                          </div>
                        ) : (
                          <div className={T.detailCard}>
                            <div className="text-sm font-semibold text-slate-900">Details</div>

                            <div className="mt-4">
                              {r.kind === "add"
                                ? renderLegCards(r.deposits, {
                                    border: "border-emerald-200",
                                    bg: "bg-emerald-50",
                                    title: "text-emerald-900",
                                    titleText: "Deposit"
                                  }, isMasked)
                                : r.kind === "remove"
                                  ? renderLegCards(r.withdrawals, {
                                      border: "border-sky-200",
                                      bg: "bg-sky-50",
                                      title: "text-sky-900",
                                      titleText: "Withdraw"
                                    }, isMasked)
                                  : null}
                            </div>

                            {r.kind === "remove" ? (
                              <div className="mt-3 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 shadow-sm">
                                <div className="font-semibold text-slate-900">
                                  {language === "en" ? "Note (Remove Liquidity)" : "Hinweis (Remove Liquidity)"}
                                </div>
                                <div className="mt-0.5">
                                  {language === "en"
                                    ? (isUK
                                      ? "Withdraw legs are valued at transaction time (market price). The taxable P/L below is based on the Section 104 cost basis of the LP position (no mark-to-market valuation of the LP token)."
                                      : "Withdraw legs are valued at transaction time (market price). The taxable P/L below is based on the FIFO cost basis of the LP position (no mark-to-market valuation of the LP token).")
                                    : (isUK
                                      ? "Withdraw-Legs werden zum TX-Zeitpunkt bewertet (Marktpreis). Der steuerliche G/V unten basiert auf dem Section-104-Einstand der LP-Position (kein Mark-to-Market des LP-Tokens)."
                                      : "Withdraw-Legs werden zum TX-Zeitpunkt bewertet (Marktpreis). Der steuerliche G/V unten basiert auf dem FIFO-Einstand der LP-Position (kein Mark-to-Market des LP-Tokens).")}
                                </div>
                              </div>
                            ) : null}

                            <div className="mt-6">
                              <div className="text-sm font-semibold text-gray-900">
                                {taxLabel} (Liquidity)
                              </div>

                              <div className="mt-3 space-y-4">
                                {(r.disposals || []).length === 0 ? (
                                  <div className="text-xs text-gray-500">
                                    {language === "en" ? "No disposals available." : "Keine Disposals vorhanden."}
                                  </div>
                                ) : (
                                  (r.disposals || []).map((d, i) => {
                                    const dtx = buildDisposalTx(d, r.timestamp, r.txid);
                                    const inflows = Array.isArray(dtx.__inflows) ? dtx.__inflows : [];
                                    return (
                                      <div key={`${dtx.token || "token"}-${i}`} className="rounded-lg border border-gray-200 bg-white">
                                        <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
                                          <div className="text-xs font-semibold text-gray-800">
                                            {dtx.token || "—"}{" "}
                                            <span className="font-mono font-normal">{dtx.amount}</span>
                                          </div>
                                        </div>

                                        <div className="p-2">
                                          <table className="w-full">
                                            <tbody>
                                              <FifoDetailRow
                                                tx={dtx}
                                                inflows={inflows}
                                                colSpan={1}
                                                fullPrecisionAmounts={true}
                                                fullPrecisionDp={18}
                                                selectedCountry={selectedCountry}
                                                isPreviewMode={isPreviewMode}
                                                maskRowEur={isMasked}
                                                pdfExportMode={pdfExportMode}
                                                language={language}
                                              />
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    );
                                  })
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  ) : null}
                  <PreviewModeSeparator
                    show={shouldShowPreviewSeparatorAfterRow(index, filtered.length, isPreviewMode)}
                    colSpan={8}
                    language={language}
                    currencyCode={currencyCode}
                  />
                </>
              );

              if (virtualMode) {
                return <React.Fragment key={rowKey}>{rowInner}</React.Fragment>;
              }

              return (
                <React.Fragment key={rowKey}>
                  <tbody className={cn("pdf-row-group", T.tbody)}>
                    {rowInner}
                  </tbody>
                  {pdfExportMode && index < filtered.length - 1 ? (
                    <tbody className="pdf-tx-separator" aria-hidden="true">
                      <tr>
                        <td colSpan={8} className="pdf-tx-separator-cell" />
                      </tr>
                    </tbody>
                  ) : null}
                </React.Fragment>
              );
  };

  const liquidityColgroup = (
    <colgroup>
      <col style={{ width: "14%" }} />
      <col style={{ width: "14%" }} />
      <col style={{ width: "12%" }} />
      <col style={{ width: "14%" }} />
      <col style={{ width: "14%" }} />
      <col style={{ width: "12%" }} />
      <col style={{ width: "10%" }} />
      <col style={{ width: "10%" }} />
    </colgroup>
  );

  const liquidityThead = (
    <thead className={cn(T.thead, primaryTableHeadClass, pdfExportMode && "print:bg-white")}>
      <tr>
        <th className={cn(T.th, "text-center")}><span className={T.thInnerCenter}><span className={cn(T.thLabel, "v3-th-label-fix")}>{t("type", language)}</span></span></th>
        <th className={cn(T.th, "text-center")}><span className={T.thInnerCenter}><span className={cn(T.thLabel, "v3-th-label-fix")}>{t("txHashColumn", language)}</span></span></th>
        <th className={cn(T.th, "text-center")}><span className={T.thInnerCenter}><span className={cn(T.thLabel, "v3-th-label-fix")}>Pool</span></span></th>
        <th className={cn(T.th, "text-center")}><span className={T.thInnerCenter}><span className={cn(T.thLabel, "v3-th-label-fix")}>{t("lpDepositColumn", language)}</span><span className={cn(T.thSub, "v3-th-sub-fix")}>{t("tokensColumnSub", language)}</span></span></th>
        <th className={cn(T.th, "text-center")}><span className={T.thInnerCenter}><span className={cn(T.thLabel, "v3-th-label-fix")}>{t("lpWithdrawColumn", language)}</span><span className={cn(T.thSub, "v3-th-sub-fix")}>{t("tokensColumnSub", language)}</span></span></th>
        <th className={cn(T.th, "text-center")}><span className={T.thInnerCenter}><span className={cn(T.thLabel, "v3-th-label-fix")}>LP</span></span></th>
        <th className={cn(T.th, "text-center")}><span className={T.thInnerCenter}><span className={cn(T.thLabel, "v3-th-label-fix")}>{t("taxableProfitShort", language)}</span><span className={cn(T.thSub, "v3-th-sub-fix")}>({currencyCode})</span></span></th>
        <th className={cn(T.th, "text-center")}><span className={T.thInnerCenter}><span className={cn(T.thLabel, "v3-th-label-fix")}>{pdfExportMode ? (language === "en" ? "Date (UTC)" : "Datum (UTC)") : t("date", language)}</span></span></th>
      </tr>
    </thead>
  );

  return (
    <section
      className={cn(
        T.shell,
        pdfExportMode && "rounded-none border-0 bg-white shadow-none ring-0"
      )}
    >
      <div className={cn(T.header, T.headerRail, tone.rail)}>
        <div className={T.headerInner}>
          <div className={T.headerMain}>
            <span className={T.eyebrow}>
              <LayoutDashboard className={cn("h-3.5 w-3.5", tone.eyebrow)} />
              {tr("taxYear", "Steuerjahr")} {yearLabel}
            </span>
            <h2 className={T.title}>{tr("liquidityPools", "LIQUIDITY POOLS")}</h2>
            <p className={T.subtitle}>
              {tr("liquiditySubtitle", "Add/Remove Liquidity (Token-Legs + FIFO Disposals)")}
            </p>
          </div>
          {!pdfExportMode && (
            <div className={T.headerAsidePills}>
              <span className={cn(T.headerChipSoft, "text-violet-700 border-violet-200 bg-violet-50/80 uppercase tracking-[0.12em]")}>
                <span className="h-2 w-2 rounded-full bg-violet-500" />
                <span>LP</span>
                <span className="h-3.5 w-px bg-violet-200" />
                <span>Pool</span>
              </span>
            </div>
          )}
        </div>
      </div>

      {!pdfExportMode && (
        <div className={T.toolbarWrap}>
          <div className={cn(T.toolbar, "flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between")}>
            <div className="flex-1 min-w-0">
              <input
                className={T.input}
                placeholder={language === "en" ? "Search: TX hash, pool, token (VET/VTHO/VIP-180)…" : "Suchen: TX-Hash, Pool, Token (VET/VTHO/VIP-180)…"}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <span className={T.countPill}>
                <span className="h-2 w-2 rounded-full bg-emerald-500" />
                <span className="font-semibold text-slate-800">{filtered.length}</span>
                <span>{tr("transactions", "Transaktionen")}</span>
              </span>
              <select className={T.select} value={poolFilter} onChange={(e) => setPoolFilter(e.target.value)}>
                <option value="all">{language === "en" ? "All Pools" : "Alle Pools"}</option>
                {pools.filter((p) => p !== "all").map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
              <select className={T.select} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                <option value="all">{language === "en" ? "All Types" : "Alle Typen"}</option>
                <option value="add">{language === "en" ? "Add" : "Add"}</option>
                <option value="remove">{language === "en" ? "Remove" : "Remove"}</option>
              </select>
            </div>
          </div>
        </div>
      )}

      <PreviewTableNotice
        isPreviewMode={isPreviewMode}
        totalRows={filtered.length}
        language={language}
        currencyCode={currencyCode}
      />

      <div className={T.tableBlock}>
        <div className={cn(T.tableWrap, primaryTableWrapClass)}>
          {filtered.length === 0 ? (
            <table className={cn(T.table, "min-w-[1220px] table-fixed print:min-w-0", pdfExportMode && "v3-pdf-tx-blocks")}>
              {liquidityColgroup}
              {liquidityThead}
              <tbody className={T.tbody}>
                <tr>
                  <td colSpan={8} className="px-6 py-10">
                    {!pdfExportMode ? (
                      <UnifiedTableEmptyState
                        language={language}
                        year={String(yearLabel ?? "")}
                        scopeLabel={language === "en" ? "Liquidity" : "Liquidität"}
                        totalCount={emptyMetrics.totalCount}
                        yearCount={emptyMetrics.yearCount}
                        otherYearsCount={emptyMetrics.otherYearsCount}
                        hiddenByFilterCount={emptyMetrics.hiddenByFilterCount}
                        inTable
                      />
                    ) : null}
                  </td>
                </tr>
                {renderLiquidityFooter()}
              </tbody>
            </table>
          ) : (
            <table className={cn(T.table, "min-w-[1220px] table-fixed print:min-w-0", pdfExportMode && "v3-pdf-tx-blocks")}>
              {liquidityColgroup}
              {liquidityThead}
              {filtered.map((r, index) => renderLiquidityRow(r, index))}
              <tbody className={T.tbody}>{renderLiquidityFooter()}</tbody>
            </table>
          )}
        </div>
      </div>
    </section>
  );
}
